import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { applyFilterMutation } from 'gqlQueries/filterQueries';
import { useLocation } from 'react-router-dom';
import { UserContext } from 'contexts/ConfigContext';
import { deleteTypeNameFromResponse, getConfig, getScreenFilterDetails } from 'utils/helperMethods';
import _, { isNil } from 'lodash';

const errorMessage: string = 'Something went wrong!';

interface Props {
    onComplete: Function;
    onError?: Function;
    updateTimeFilterFrequency?: Function;
}

function useApplyFilterMutation({ onComplete, onError, updateTimeFilterFrequency }: Props) {
    let route = useLocation();
    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const { screenName, screenFilterConfig, currentScreenFeatureName } = getScreenFilterDetails(
        route,
        globalFilterConfig,
    );
    const [mutateApplyFilter, { data: applyFilterData, loading: applyFilterLoading, error: applyFilterError }] =
        useMutation(applyFilterMutation, {
            onCompleted: onFetchComplete,
            onError: onFetchError,
        });
    useEffect(() => {
        setLoading(applyFilterLoading);
    }, [applyFilterLoading]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            //get frequency intersection
            const availableFrequenciesFromResponse = getFrequencyIntersection(payload);
            if (updateTimeFilterFrequency) updateTimeFilterFrequency(availableFrequenciesFromResponse);
            deleteTypeNameFromResponse(payload);
            const tempData = onComplete(payload);
            if (tempData) setData(tempData);
        } catch (error) {
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setError(error);
        //sending empty data to error to avoid null data being sent
        if (onError) onError();
        setData([]);
        setLoading(false);
    }

    return {
        loading,
        error,
        data,
        applyFilterMutate: (variables: any) => {
            const tempVariables = { ...variables };
            tempVariables.variables.featureName = currentScreenFeatureName;
            setError(null);
            mutateApplyFilter(tempVariables);
        },
    };
}

export { useApplyFilterMutation };

// Function to compute intersection
function getFrequencyIntersection(data: any) {
    // Extract frequencies into an array
    const frequencies = Object.values(data)
        .map((dim: any) => dim?.availableFrequencies)
        .filter((freq) => !isNil(freq) && (!Array.isArray(freq) || freq.length > 0));
    if (frequencies.length === 0) {
        return []; // No frequencies to intersect
    }

    // Compute intersection using reduce
    return frequencies.reduce((acc, curr) => {
        return acc.filter((item: string) => curr.includes(item));
    });
}
